/**
 * 帧级调度抽象：主线程 store 通知合并与 Worker 推送合并共用同一个接口。
 *
 * 约定：调用 `schedule(flush)` 后在「下一帧」异步执行 flush 恰好一次。
 * 去重（同一帧内只 schedule 一次）是调用方的职责，调度器本身不去重。
 */
export type Scheduler = (flush: () => void) => void;

/** 后台标签页 rAF 停摆时的兜底间隔：逻辑订阅仍需推进（如未读数、标题栏），但不必逐帧。 */
const HIDDEN_FALLBACK_MS = 200;

/** Worker 等无 rAF 环境下近似一帧的间隔。 */
const FRAME_APPROX_MS = 16;

/**
 * 生产环境调度器：
 * - 主线程：rAF（与渲染对齐，天然合并到帧）+ 兜底 timeout——页面切后台后浏览器
 *   停发 rAF，没有兜底的话已排队的 flush 会冻结到回前台才执行。
 * - Worker / 无 rAF 环境：16ms timeout 近似一帧。
 */
export function createFrameScheduler(): Scheduler {
  if (typeof requestAnimationFrame !== 'function') {
    return (flush) => {
      setTimeout(flush, FRAME_APPROX_MS);
    };
  }
  return (flush) => {
    let fired = false;
    const run = (): void => {
      if (fired) return;
      fired = true;
      cancelAnimationFrame(rafId);
      clearTimeout(timerId);
      flush();
    };
    const rafId = requestAnimationFrame(run);
    const timerId = setTimeout(run, HIDDEN_FALLBACK_MS);
  };
}

export interface ManualScheduler {
  scheduler: Scheduler;
  /** 执行当前已排队的全部 flush（flush 期间新排入的留到下一次 run）。 */
  run(): void;
  pending(): number;
}

/** 测试用手动调度器：schedule 只入队，调用 run() 才执行。 */
export function createManualScheduler(): ManualScheduler {
  let queue: Array<() => void> = [];
  return {
    scheduler: (flush) => {
      queue.push(flush);
    },
    run() {
      const batch = queue;
      queue = [];
      for (const flush of batch) flush();
    },
    pending: () => queue.length,
  };
}
